import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { clearLocalStorageItems, formatDate } from "../utils/global";
import { useOrganisationsStore } from "../stores/organisations-store";
import { useServicesStore } from "../stores/options-store";

import { ICONS } from "../utils/assets";

import AuthenticatedTemplate from "../components/AuthenticatedTemplate";
import { DashboardNoUpdates } from "../components/DashboardNoUpdates";
import { SideBar } from "../components/SideBar";
import { FooterDashboard } from "../components/FooterDashboard";
import Header from "../components/Header";
import Breadcrumbs from "../components/Breadcrumbs";
import Button from "../components/form/Button";
import OrganisationCard from "../components/OrganisationCard";

export const DashboardReturn = () => {
  const navigate = useNavigate();
  const { clearOrganisations } = useOrganisationsStore();
  const { clearServices } = useServicesStore();
  const [orderDate, setOrderDate] = useState("");
  const [orderDashboard, setOrderDashboard] = useState([]);
  const [displayName, setDisplayName] = useState("");
  const [breadcrumbs] = useState([
    {
      name: "Dashboard",
    },
  ]);

  useEffect(() => {
    const runOrder = async () => {
      try {
        // Await the fetch operation
        await fetchGetDashboardOrder();

        // Once fetch is complete, set data details
        await setDataDetails();
      } catch (error) {
        console.error("Error fetching order data:", error);
      }
    };

    // Call the async function
    runOrder();
  }, []);
  useEffect(() => {}, [orderDate]);

  /**
   * Function to get the order by customer email
   */
  async function fetchGetDashboardOrder() {
    try {
      // Retrieve email from Session storage
      const customerInfo = sessionStorage.getItem("LS_CUSTOMER_INFO");
      if (!customerInfo) {
        console.log("No customer info found in sessionStorage.");
        return;
      }

      const { email, displayName } = JSON.parse(customerInfo);
      setDisplayName(displayName);

      if (!email) {
        console.log("No email found in the customer info.");
        return;
      }

      // Send the POST request
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/dashboard/get-dashboard-order`,
        {
          email,
        }
      );

      if (response.data) {
        // Update state and sessionStorage
        setOrderDashboard(response.data);
        sessionStorage.setItem(
          "LS_DASHBOARD_ORDER",
          JSON.stringify(response.data)
        );
      } else {
        console.log("No data returned from the server.");
      }
    } catch (error) {
      // Handle errors
      setOrderDashboard([]);
      console.error(
        "An error occurred while fetching the dashboard order:",
        error
      );
      if (error.response && error.response.data) {
        console.error("Server error:", error.response.data);
        setOrderDashboard([]);
      }
    }
  }
  /**
   * Function to get the request order date and display in the screen
   */
  async function setDataDetails() {
    const LS_DASHBOARD_ORDER = sessionStorage.getItem("LS_DASHBOARD_ORDER");
    if (LS_DASHBOARD_ORDER) {
      const dashboardOrder = JSON.parse(LS_DASHBOARD_ORDER);

      const formattedDate = formatDate(
        dashboardOrder[0].orderRequestedDate,
        "en-US",
        "UTC"
      );
      setOrderDate(formattedDate);
    }
  }

  function handleUpdateNow() {
    clearLocalStorageItems();
    clearOrganisations();
    clearServices();
    navigate("/dashboard-welcome");
  }

  return (
    <>
      <AuthenticatedTemplate>
        <SideBar />
        <Header
          title={"Welcome back, " + displayName}
          breadcrumbs={breadcrumbs}
        />

        <main className="dashboard">
          <div className="topic-title">
            <h1 className="text-sm text-neutral-950">
              Welcome back, {orderDashboard[0]?.customerDisplayName}
            </h1>
            <Breadcrumbs items={breadcrumbs} />
          </div>

          {orderDashboard[0] ? (
            <div className="dashboard-home">
              <div className="dashboard-following">
                <h2 className="paragraph-lg semibold text-neutral-950">
                  Following your organisations(s)
                </h2>
                <div className="dashboard-following__info">
                  <p className="small text-neutral">
                    Order number:{" "}
                    <span className="semibold text-neutral-950">
                      {orderDashboard[0]?.orderNumber || "N/A"}
                    </span>
                  </p>
                  <p className="small text-neutral">
                    Submission date:{" "}
                    <span className="semibold text-neutral-950">
                      {orderDate || "N/A"}
                    </span>
                  </p>
                </div>
              </div>

              {orderDashboard.length
                ? orderDashboard.map((items, index) => (
                    <div className="dashboard-organisations" key={index + 100}>
                      {items.orderLines.map((item, index) => {
                        return (
                          <OrganisationCard
                            key={index}
                            accountId={item.Account_ID}
                            status={item.Line_Status}
                            statusDescription={item.Line_Status_Descr}
                            name={item.Organisation_Name}
                            logo={item.Organisation_Logo_Path}
                          />
                        );
                      })}
                    </div>
                  ))
                : null}

              <h2 className="paragraph-lg semibold text-neutral-950">
                Your Details
              </h2>

              <div className="dashboard-details">
                <div className="dashboard-details__card">
                  <h3 className="dashboard-details__title paragraph-md semibold text-neutral-950">
                    Changed from
                  </h3>
                  <ul className="dashboard-details__info">
                    {orderDashboard[0]?.nameFrom ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.USER_ICON} alt="User icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Name
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0].nameFrom}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.genderFrom ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.GENDER_ICON} alt="Gender icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Gender
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0].genderFrom}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.addressFrom ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.ADDRESS_ICON} alt="Address icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Address
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0].addressFrom}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.emailFrom ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.EMAIL_ICON} alt="E-mail icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            E-mail
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0]?.emailFrom}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.numberFrom ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.PHONE_ICON} alt="Phone icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Phone
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0]?.numberFrom}
                          </p>
                        </div>
                      </li>
                    ) : null}
                  </ul>
                </div>

                <div className="dashboard-details__card">
                  <h3 className="dashboard-details__title paragraph-md semibold text-neutral-950">
                    To
                  </h3>
                  <ul className="dashboard-details__info">
                    {orderDashboard[0]?.nameTo ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.USER_ICON} alt="User icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Name
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0]?.nameTo}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.genderTo ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.GENDER_ICON} alt="Gender icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Gender
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0]?.genderTo}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.addressTo ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.ADDRESS_ICON} alt="Address icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Address
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0]?.addressTo}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.emailTo ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.EMAIL_ICON} alt="E-mail icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            E-mail
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0]?.emailTo}
                          </p>
                        </div>
                      </li>
                    ) : null}
                    {orderDashboard[0]?.numberTo ? (
                      <li className="dashboard-details__item">
                        <img src={ICONS.PHONE_ICON} alt="Phone icon" />
                        <div>
                          <h3 className="paragraph-md semibold paragraph-md--dark">
                            Phone
                          </h3>
                          <p className="paragraph-md">
                            {orderDashboard[0]?.numberTo}
                          </p>
                        </div>
                      </li>
                    ) : null}
                  </ul>
                </div>
              </div>

              <div className="dashboard-details__update">
                <div>
                  <h4 className="paragraph-md semibold text-neutral-950">
                    Update
                  </h4>
                  <p className="paragraph-md">
                    Do you need to update any other personal details?
                  </p>
                </div>
                <Button variant="outlined" onClick={() => handleUpdateNow()}>
                  Update now
                </Button>
              </div>
            </div>
          ) : (
            <DashboardNoUpdates />
          )}
        </main>

        <FooterDashboard />
      </AuthenticatedTemplate>
    </>
  );
};
